import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { request } from "@/api/client";
import { AdminUserIdentitySchema } from "@/api/admin/users";
import { SUPPORTED_LOCALES, type SupportedLocale } from "@/i18n";

/**
 * DUE-192: the author is a full identity block, not a `tg_id`.
 *
 * The previous shape declared `user_tg_id` non-nullable while the backend
 * had it nullable all along, so a single ticket from an iOS guest failed
 * zod for the whole array and blanked the tab.
 */
const FeedbackListItemSchema = z.object({
  id: z.string(),
  user: AdminUserIdentitySchema,
  locale: z.enum(SUPPORTED_LOCALES as unknown as [SupportedLocale, ...SupportedLocale[]]),
  category: z.enum(["question", "bug", "payment", "idea", "other"]),
  body: z.string(),
  created_at: z.string(),
});
const FeedbackListSchema = z.object({
  items: z.array(FeedbackListItemSchema),
});
export type FeedbackListItem = z.infer<typeof FeedbackListItemSchema>;

const FeedbackDTOSchema = z.object({
  id: z.string(),
  status: z.enum(["open", "answered", "closed"]),
  created_at: z.string(),
});

export function useOpenFeedback() {
  return useQuery({
    queryKey: ["admin", "feedback", "open"],
    queryFn: ({ signal }) =>
      request({
        path: "/api/admin/feedback?status=open",
        schema: FeedbackListSchema,
        signal,
      }),
  });
}

export function useReplyFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; text: string }) =>
      request({
        method: "POST",
        path: `/api/admin/feedback/${encodeURIComponent(args.id)}/reply`,
        body: { text: args.text },
        schema: FeedbackDTOSchema,
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["admin", "feedback", "open"] }),
  });
}
